import { intersect, intersectStrict, getIntersection as number_getIntersection } from "./number.tools";
import { UnixTimestamp } from "./timestamps.tools";

export * from "./interval.string.tools";

/** Defines an interval between two number, those number can be UnixTimestamps values ({@link timestampInterval}) */
export interface Interval<N extends number = number> {
    /** start of the interval */
    from: N;
    /** finish of the interval */
    to: N;
}

export interface OpenInterval<N extends number = number> {
    /** start of the interval */
    from: N;
    /** finish of the interval */
    to?: N;
}

export type TsInterval = Interval<UnixTimestamp>;
export type OpenTsInterval = OpenInterval<UnixTimestamp>;

/**
 * Convert 2 timestamps in one Interval
 * @param from start of interval
 * @param to finish of interval
 * @returns the interval object 
 */
export function timestampInterval(from: UnixTimestamp, to: UnixTimestamp): TsInterval {
    return { from, to };
}


/** 
 * Returns interval's duration (in seconds if interval is build with {@link timestampInterval}) (to-from), ignoring negative interval with from > to
 * @param interval interval to compute duration
 * @returns number of seconds in interval
 */
export function getPositiveDuration<N extends number = number>(interval: Interval<N>): N {
    const duration = interval.to - interval.from;
    return (duration < 0 ? 0 : duration) as N;
}


/** 
 * Returns the sum of each interval duration, ignoring negative intervals with from > to
 * (ie: intervals=[{5,10}, {30, 15}, {15,25}] => return (10-5) + ((15-30) => return 0) + (25-15) = 15)
 * @param intervals intervals to sum durations from
 * @returns the sum
 */
export function getPositiveDurationsSum<N extends number = number>(intervals: Interval<N>[]): N {
    let totalDuration = 0;
    if (intervals) {
        for (const i of intervals) {
            totalDuration += getPositiveDuration(i);
        }
    }
    return totalDuration as N;
}


/**
 * Merge intervals if possible
 * [1-3],[2-5],[7-9] => [1-5],[7-9]
 * @param intervals to merge
 * @returns the minimal list of intervals from  merged intervals. 
 */
export function mergeIntervals<IN extends Interval = Interval<number>>(intervals: IN[]): IN[] {
    const tmpList = intervals.slice();// clone of intervals
    intervals = [];
    for (const toAdd of tmpList) {
        addInterval(toAdd, intervals);
    }
    return intervals;
}

/**
 * Merge intervals distant from less than margin
 * [10-15],[20-50] with 5 margin => [10-50]
 * @param intervals to merge
 * @param margin max (included) distance between intervals boundaries that allows merging
 * @returns the minimal list of intervals from  merged intervals. 
 */
export function mergeIntervalsWithMargin<IN extends Interval = Interval<number>>(intervals: IN[], margin: number): IN[] {
    if (!intervals?.length) { // Return intervals if intervals is empty
        return intervals;
    }

    // -- Sort intervals by "from" field --
    const clonedIntervals = intervals.slice();// clone
    clonedIntervals.sort((a, b) => { return a.from - b.from; });

    // -- Prepare result --
    intervals = [];

    // -- Merge intervals --
    // merged will be used to compare previous interval and current one, it will be updated if needed
    let merged: IN | null = null;
    for (const currentInterval of clonedIntervals) {
        if (merged == null) {
            // No merged interval yet
            merged = currentInterval;
        } else if (merged.to + margin >= currentInterval.from) {
            // Merged interval intersects current
            merged = { from: Math.min(merged.from, currentInterval.from), to: Math.max(merged.to, currentInterval.to) } as IN;
        } else {
            // We do not intersect, add previous interval to list
            intervals.push(merged);
            merged = currentInterval;
        }
    }
    if (merged != null) {
        intervals.push(merged);
    }
    return intervals;
}


/**
 * Adds an interval to the intervals list
 * if the added interval can be merged with another one, they will be merged
 * else, it's added at the end of array
 * [1-4] [5-6] + [3-5] => [1-6]
 * @param toAdd interval to add
 * @param intervalsByRef interval list that will be modified
 * @returns intervalByRef that has been modified
 */
export function addInterval<IN extends Interval = Interval<number>>(toAdd: IN, intervalsByRef: IN[]): IN[] {

    // If toAdd has width 0, do nothing
    if (toAdd.to - toAdd.from === 0) {
        return intervalsByRef;
    }

    // check if toAdd is not already in intervals
    for (const d of intervalsByRef) {
        if (contains(d, toAdd)) {
            return intervalsByRef;
        }
    }

    // merge or remove intervals intersecting toAdd
    for (let i = 0; i < intervalsByRef.length; i++) {
        const interval: Interval = intervalsByRef[i];
        if (contains(toAdd, interval)) {
            // remove intervals that are contained into toAdd
            intervalsByRef.splice(i, 1);
            i--;
        } else if (intersect(interval.from, interval.to, toAdd.from, toAdd.to)) {
            // merge
            intervalsByRef.splice(i, 1);
            return addInterval({
                from: Math.min(toAdd.from, interval.from),
                to: Math.max(toAdd.to, interval.to)
            } as IN, intervalsByRef);
        }
    }
    intervalsByRef.push(toAdd);
    return intervalsByRef;
}

/**
 * removes an interval from the intervals list
 * [1-4] [5-6] [7-9] - [3-8] => [1-3] [8-9]
 * @param toRemove interval to remove
 * @param intervalsByRef interval list that will be modified
 * @returns intervalByRef that has been modified
 */
export function removeInterval<IN extends OpenInterval<N>, N extends number = number>(toRemove: IN, intervalsByRef: IN[]): IN[] {
    // If toRemove has width 0, do nothing
    if (toRemove.to != null && toRemove.to - toRemove.from === 0) {
        return intervalsByRef;
    }

    for (let i = 0; i < intervalsByRef.length; i++) {
        const currentInterval = intervalsByRef[i];
        // If toRemove contains currentInterval, remove currentInterval from intervals
        if (contains(toRemove, currentInterval)) {
            intervalsByRef.splice(i, 1);
            i--;
        } else if (intersectStrict<N>(currentInterval.from, currentInterval.to, toRemove.from, toRemove.to)) { // If there is an intersection between currentInterval and toRemove
            // Remove it from intervals and create a new updated interval
            intervalsByRef.splice(i, 1);

            // Handle open ends on the given intervals and use Infinity to keep the comparison logic
            const toRemoveTo = toRemove.to ?? Number.POSITIVE_INFINITY;
            const currentIntervalTo = currentInterval.to ?? Number.POSITIVE_INFINITY;

            if (currentIntervalTo > toRemoveTo && currentInterval.from < toRemoveTo) {
                const afterToRemove = { from: toRemoveTo, to: currentInterval.to } as IN;
                intervalsByRef.splice(i, 0, afterToRemove);
            }
            if (currentInterval.from < toRemove.from && currentIntervalTo > toRemove.from) {
                const beforeToRemove = { from: currentInterval.from, to: toRemove.from } as IN;
                intervalsByRef.splice(i, 0, beforeToRemove);
            }
        }
    }
    return intervalsByRef;
}

/**
 * Removes all intervals from the intervals list
 * Will not merge intervals if possible
 * [1-4] [5-6] [7-9] - [3-8] => [1-3] [8-9]
 * @param intervalsToRemove intervals to remove
 * @param intervalsByRef interval list that will be modified
 * @returns intervalByRef that has been modified
 */
export function removeAllIntervals<IN extends OpenInterval = Interval<number>>(intervalsToRemove: IN[], intervalsByRef: IN[]): IN[] {
    if (!intervalsToRemove) { return intervalsByRef; }
    for (const toRemove of intervalsToRemove) {
        removeInterval(toRemove, intervalsByRef);
    }
    return intervalsByRef;
}


/**
 * @param interval1
 * @param interval2
 * @param strict if true, if the intersection is a single instant getIntersection will return null
 * @returns [begin, end] of intersection or null if no intersection
 *
 * ex : (1,3) and (2,4) => (2,3)
 * ex : (1,2) and (2,3) => null if strict, (2,2) otherwise
 */
export function getIntersection<IN extends OpenInterval = Interval<number>>(interval1: IN, interval2: IN, strict: boolean): IN | null {
    if (interval1 == null || interval2 == null) {
        return null;
    }
    // Check intersection between numbers
    const res = number_getIntersection(interval1.from, interval1.to, interval2.from, interval2.to, strict);
    if (res != null) {
        return { from: res.begin, to: res.end } as IN;
    }
    return null;
}


/**
 * Returns a list of common intervals in intervals1 and intervals2
 * Returns null if there is no interval in common
 * @param intervals1 
 * @param intervals2 
 * @param strict  if true, if an intersection is a single instant it won't be in returned array
 * @param doNotMergeIntervals if true, Do not merge intersected intervals
 * @returns 
 */
export function getIntersectionArrays<IN extends Interval = Interval<number>>(intervals1: IN[], intervals2: IN[], strict = true, doNotMergeIntervals?: boolean): IN[] | null {
    if (intervals1 == null || intervals2 == null) {
        return null;
    }
    let result: IN[] | null = null;
    for (const i1 of intervals1) {
        for (const i2 of intervals2) {
            // Check intersection between all intervals in intervals1 and all intervals in intervals2
            const inter = getIntersection(i1, i2, strict);
            if (inter != null) {
                if (result == null) {
                    result = [];
                }

                // Do not merge intersected intervals if option doNotMergeIntervals is passed
                if (doNotMergeIntervals) {
                    result.push(inter);
                } else {
                    addInterval(inter, result);
                }
            }
        }
    }
    return result;
}

/**
 * Return only intervals which are exclusive to firstIntervals or exclusive to secondIntervals 
 * @param firstIntervals 
 * @param secondIntervals 
 * @returns 
 */
export function xor<IN extends Interval = Interval<number>>(firstIntervals: IN[], secondIntervals: IN[]): IN[] {
    const cloneFirstIntervals = firstIntervals.slice();// clone;
    const cloneSecondIntervals = secondIntervals.slice();// clone
    // Remove all intervals of secondIntervals in a copy of firstIntervals
    const diffPrepared = removeAllIntervals(secondIntervals, cloneFirstIntervals);
    // Remove all intervals of firstIntervals in a copy of secondIntervals
    const diffDeclared = removeAllIntervals(firstIntervals, cloneSecondIntervals);

    // Concat the two arrays and sort intervals by "from" field
    let concatIntervals = diffPrepared.concat(diffDeclared);
    concatIntervals = concatIntervals.sort((i1: IN, i2: IN) => {
        return i1.from - i2.from;
    });
    // Merged intervals 
    return mergeIntervals(concatIntervals);
}


/**
 * Returns true if one interval on globals contains inside ({@link contains})
 * (ie: globals: [{1,5}], inside = {2,3}, return true)
 * @param globals list of intervals
 * @param inside interval to test
 * @returns wether globals contains inside
 */
export function arrayContains<IN extends OpenInterval = OpenInterval<number>>(globals: IN[], inside: IN): boolean {
    if (globals == null || inside == null) {
        return false;
    }
    for (const global of globals) {
        if (contains(global, inside)) {
            return true;
        }
    }
    return false;
}

/**
 * Return true if global contains inside (not strict)
 * (ie: global: {1,5}, inside = {2,3}, return true, global: {1,5}, inside = {2,5} return true)
 * @param global intervals
 * @param inside interval to test
 * @returns wether globals contains inside
 */
export function contains<N extends number = number>(global: OpenInterval<N>, inside: OpenInterval<N>): boolean {
    if (global == null || inside == null) {
        return false;
    }

    // If inside is open interval but global is bounded, it can't be fully contained
    if (inside.to == null && global.to != null) {
        return false;
    }

    // Use Infinity to handle an open-ended interval
    const globalTo = global.to ?? Number.POSITIVE_INFINITY;
    const insideTo = inside.to ?? Number.POSITIVE_INFINITY;

    return global.from <= inside.from && globalTo >= insideTo;
}

/** @returns bounding interval for all provided intervals or undefined if no interval */
export function getBoundingInterval<IN extends Interval = Interval<number>>(intervals: IN[]): IN | undefined {
    if (intervals.length === 0) { return undefined; }
    const result = { from: intervals[0].from, to: intervals[0].to } as IN;
    for (let i = 1; i < intervals.length; i++) {
        const interval = intervals[i];
        if (result.from > interval.from) { result.from = interval.from; }
        if (result.to < interval.to) { result.to = interval.to; }
    }
    return result;
}